import React, { useState } from 'react';
import { useConstructorStore } from '../store/constructorStore';
import { t } from '../i18n';
import { createDraft, createPayment, checkPromocode } from '../api/client';

export const Step5Preview: React.FC = () => {
  const {
    business_name, business_description, phone, items, bg_color, text_color,
    country, city, language, promocode, promocodeValid,
    setField, setPromocodeValid, setLoading, setError, setDraftResult,
    getLandingData, getComponents,
  } = useConstructorStore();

  const [promoInput, setPromoInput] = useState('');
  const [checkingPromo, setCheckingPromo] = useState(false);
  const [processing, setProcessing] = useState(false);

  const tg = (window as any).Telegram?.WebApp;
  const telegramId = tg?.initDataUnsafe?.user?.id;

  const handleCheckPromo = async () => {
    if (!promoInput) return;
    setCheckingPromo(true);
    try {
      const result = await checkPromocode(promoInput, telegramId);
      if (result.valid) {
        setField('promocode', promoInput);
        setPromocodeValid(true, result.remaining_uses);
      } else {
        setPromocodeValid(false);
        alert(result.reason || 'Промокод недействителен');
      }
    } catch (e: any) {
      alert(e.message);
    } finally {
      setCheckingPromo(false);
    }
  };

  const handlePay = async () => {
    if (!telegramId) {
      alert('Откройте через Telegram бота');
      return;
    }

    setProcessing(true);
    setLoading(true);

    try {
      // Генерируем slug
      const slug = `${country}-${city}-${Date.now().toString(36)}`;
      useConstructorStore.getState().setField('slug', slug);

      // Создаём черновик
      const draft = await createDraft({
        telegram_id: telegramId,
        title: business_name || 'Мой лендинг',
        country,
        city,
        slug,
        data: getLandingData(),
        components: getComponents(),
      });

      setDraftResult(draft.landing_id, draft.slug);

      // Создаём оплату (с промокодом если есть)
      const payment = await createPayment(
        telegramId,
        draft.landing_id,
        promocodeValid ? promocode : undefined
      );

      // Если бесплатно (промокод) — сразу успех
      if (payment.free) {
        alert('🎉 Лендинг создан бесплатно!\n\n🔗 ' + payment.url);
        return;
      }

      // Если платно — открываем инвойс
      if (payment.invoice_url) {
        tg.openInvoice(payment.invoice_url, async (status: string) => {
          if (status === 'paid') {
            alert('✅ Оплата прошла! Лендинг создан.');
          } else {
            alert('Оплата не завершена. Попробуйте снова.');
          }
        });
      }
    } catch (e: any) {
      setError(e.message);
      console.error('Payment error:', e);
      // Показываем детальный алерт для отладки
      alert('❌ Ошибка:\n' + e.message);
    } finally {
      setProcessing(false);
      setLoading(false);
    }
  };

  const previewStyle: React.CSSProperties = {
    background: '#f8f9fa', borderRadius: 12, padding: 16, marginBottom: 20,
  };

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 20 }}>{t('step5_title', language)}</h2>

      {/* Предпросмотр */}
      <div style={previewStyle}>
        <h3 style={{ fontSize: 14, color: '#666', marginBottom: 12 }}>{t('preview', language)}</h3>

        {/* Шапка */}
        <div style={{ background: bg_color, color: text_color, padding: 20, borderRadius: 8, textAlign: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: '0 0 8px', fontSize: 20 }}>{business_name || 'Название'}</h3>
          <p style={{ margin: 0, fontSize: 14, opacity: 0.85 }}>{business_description || 'Описание'}</p>
        </div>

        {/* Товары */}
        {items.filter(i => i.name && i.price).length > 0 && (
          <div style={{ background: 'white', borderRadius: 8, padding: 12, marginBottom: 12 }}>
            <h4 style={{ fontSize: 16, marginBottom: 8 }}>Наши услуги</h4>
            {items.filter(i => i.name && i.price).map((item, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                <span>{item.name}</span>
                <strong>{item.price}</strong>
              </div>
            ))}
          </div>
        )}

        {/* Контакты */}
        {phone && (
          <div style={{ background: 'white', borderRadius: 8, padding: 12 }}>
            <h4 style={{ fontSize: 16, marginBottom: 8 }}>Контакты</h4>
            <p style={{ margin: '4px 0' }}>📞 {phone}</p>
          </div>
        )}
      </div>

      {/* Цена */}
      <div style={{ textAlign: 'center', padding: 16, background: '#f0f0f0', borderRadius: 12, marginBottom: 16 }}>
        <p style={{ fontSize: 14, color: '#666', marginBottom: 4 }}>{t('price', language)}</p>
        <p style={{ fontSize: 28, fontWeight: 700, color: '#1a1a2e' }}>{t('price_tenge', language)}</p>
      </div>

      {/* Промокод */}
      <div style={{ marginBottom: 16 }}>
        <p style={{ fontSize: 14, color: '#666', marginBottom: 8 }}>{t('promocode_label', language)}</p>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            style={{ flex: 1, padding: '10px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 15 }}
            value={promoInput}
            onChange={(e) => setPromoInput(e.target.value)}
            placeholder={t('promocode_placeholder', language)}
          />
          <button
            onClick={handleCheckPromo}
            disabled={checkingPromo}
            style={{
              padding: '10px 16px', border: 'none', borderRadius: 8,
              background: '#1a1a2e', color: 'white', fontSize: 14, cursor: 'pointer',
            }}
          >
            {t('promocode_check', language)}
          </button>
        </div>
        {promocodeValid && (
          <p style={{ color: '#28a745', fontSize: 13, marginTop: 8 }}>
            {t('promocode_valid', language, { remaining: String(promocodeValid) })}
          </p>
        )}
      </div>

      {/* Кнопка оплаты */}
      <button
        onClick={handlePay}
        disabled={processing}
        style={{
          width: '100%', padding: 16, border: 'none', borderRadius: 12,
          background: processing ? '#999' : '#28a745',
          color: 'white', fontSize: 17, fontWeight: 700, cursor: 'pointer',
        }}
      >
        {processing ? '⏳ Обработка...' : t('pay_button', language)}
      </button>
    </div>
  );
};
